import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

const LAST_UPDATED = 'March 26, 2026';
const COMPANY = 'AdForge AI';
const CONTACT_EMAIL = 'legal@adforge.ai';

const sections = [
  {
    id: 'acceptance',
    title: '1. Acceptance of Terms',
    text: 'By accessing or using AdForge AI ("the Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, do not use the Service. These Terms apply to all visitors, users, and others who access or use AdForge AI. If you are using the Service on behalf of a business or organization, you represent that you have the authority to bind that entity to these Terms.',
  },
  {
    id: 'description',
    title: '2. Description of Service',
    text: 'AdForge AI is an AI-powered advertising platform that generates ad copy, creative images, and campaign content for businesses. The Service includes ad generation, variation creation, image regeneration, ad history, business profile management, and related features. We reserve the right to modify, suspend, or discontinue any part of the Service at any time with reasonable notice.',
  },
  {
    id: 'accounts',
    title: '3. Accounts & Registration',
    content: [
      {
        subtitle: 'Account Creation',
        text: 'You must provide accurate, complete, and current information when creating an account. You are responsible for maintaining the confidentiality of your login credentials and for all activities that occur under your account.',
      },
      {
        subtitle: 'Account Security',
        text: 'You agree to notify us immediately at legal@adforge.ai if you suspect unauthorized access to your account. AdForge AI is not liable for any loss resulting from unauthorized use of your account.',
      },
      {
        subtitle: 'One Account Per User',
        text: 'Each individual may maintain only one free account. Creating multiple free accounts to circumvent credit limits is prohibited and may result in termination of all associated accounts.',
      },
    ],
  },
  {
    id: 'plans-credits',
    title: '4. Plans, Credits & Billing',
    content: [
      {
        subtitle: 'Credit System',
        text: 'AdForge AI operates on a credit-based system. Credits are consumed when you generate ads, create variations, regenerate images, or rewrite copy. Credit costs are displayed before each action. Credits reset monthly on your billing date and do not roll over.',
      },
      {
        subtitle: 'Subscriptions',
        text: 'Paid plans are billed on a recurring monthly basis. By subscribing, you authorize AdForge AI to charge your payment method on a recurring basis. You may cancel your subscription at any time; cancellation takes effect at the end of the current billing period.',
      },
      {
        subtitle: 'Refunds',
        text: 'We offer a 7-day refund policy for new subscriptions if you have not used more than 20% of your monthly credits. Credit purchases are non-refundable. Refund requests must be submitted to legal@adforge.ai.',
      },
      {
        subtitle: 'Price Changes',
        text: 'We reserve the right to modify pricing with at least 30 days\' notice. Continued use of the Service after a price change constitutes acceptance of the new pricing.',
      },
    ],
  },
  {
    id: 'acceptable-use',
    title: '5. Acceptable Use Policy',
    intro: 'You agree not to use AdForge AI to generate content that:',
    bullets: [
      'Is false, misleading, deceptive, or fraudulent',
      'Violates any applicable law or regulation, including advertising standards',
      'Infringes any third-party intellectual property, privacy, or other rights',
      'Promotes illegal products, services, or activities',
      'Contains hate speech, harassment, threats, or discrimination based on protected characteristics',
      'Advertises adult content, weapons, or controlled substances without proper authorization',
      'Attempts to reverse-engineer, scrape, or exploit the Service\'s AI systems',
      'Interferes with or disrupts the integrity or performance of the Service',
    ],
    footer: 'We reserve the right to suspend or terminate accounts that violate this policy without prior notice.',
  },
  {
    id: 'intellectual-property',
    title: '6. Intellectual Property',
    content: [
      {
        subtitle: 'Your Content',
        text: 'You retain ownership of the business information and inputs you provide. You grant AdForge AI a limited license to use your inputs solely for the purpose of generating your requested ad content.',
      },
      {
        subtitle: 'Generated Content',
        text: 'Ad copy and images generated through the Service are provided to you for your commercial use. While we believe generated content is generally usable for advertising, we make no warranty regarding intellectual property clearance of AI-generated outputs. You are responsible for reviewing generated content before publication.',
      },
      {
        subtitle: 'AdForge AI Platform',
        text: 'The AdForge AI platform, including its software, design, trademarks, and underlying technology, is owned by AdForge AI and protected by intellectual property laws. You may not copy, modify, distribute, or create derivative works of the platform without our written permission.',
      },
    ],
  },
  {
    id: 'ai-disclaimer',
    title: '7. AI-Generated Content Disclaimer',
    text: 'AdForge AI uses artificial intelligence to generate ad copy and images. While we strive for high-quality output, AI-generated content may occasionally be inaccurate, incomplete, or unsuitable for your specific use case. You are solely responsible for reviewing, editing, and approving all generated content before use in any advertising campaign. AdForge AI does not guarantee that generated content will achieve specific advertising results or comply with all platform-specific advertising policies (e.g., Meta, Google Ads).',
  },
  {
    id: 'privacy',
    title: '8. Privacy',
    text: 'Your use of AdForge AI is also governed by our Privacy Policy, which is incorporated into these Terms by reference. By using the Service, you consent to our collection and use of data as described in the Privacy Policy.',
  },
  {
    id: 'termination',
    title: '9. Termination',
    text: 'You may delete your account at any time from the Settings page. AdForge AI may suspend or terminate your account if you violate these Terms, engage in fraudulent activity, or if required by law. Upon termination, your right to use the Service ceases immediately. We may retain certain data as required by law or for legitimate business purposes as described in our Privacy Policy.',
  },
  {
    id: 'disclaimers',
    title: '10. Disclaimers & Limitation of Liability',
    content: [
      {
        subtitle: 'No Warranty',
        text: 'THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR THAT GENERATED CONTENT WILL MEET YOUR REQUIREMENTS.',
      },
      {
        subtitle: 'Limitation of Liability',
        text: 'TO THE MAXIMUM EXTENT PERMITTED BY LAW, ADFORGE AI SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES. OUR TOTAL LIABILITY FOR ANY CLAIM ARISING FROM THESE TERMS OR YOUR USE OF THE SERVICE SHALL NOT EXCEED THE AMOUNT YOU PAID US IN THE 3 MONTHS PRECEDING THE CLAIM.',
      },
    ],
  },
  {
    id: 'governing-law',
    title: '11. Governing Law & Disputes',
    text: 'These Terms are governed by the laws of the State of Delaware, United States, without regard to conflict of law principles. Any dispute arising from these Terms shall be resolved by binding arbitration under the rules of the American Arbitration Association, except that either party may seek injunctive relief in court for intellectual property violations. YOU WAIVE YOUR RIGHT TO A JURY TRIAL OR CLASS ACTION.',
  },
  {
    id: 'changes',
    title: '12. Changes to Terms',
    text: 'We may update these Terms from time to time. We will provide at least 14 days\' notice of material changes via email or in-app notification. Continued use of the Service after changes take effect constitutes your acceptance of the revised Terms. If you do not agree to the updated Terms, you must stop using the Service.',
  },
  {
    id: 'contact',
    title: '13. Contact',
    text: `If you have questions about these Terms, please contact us at ${CONTACT_EMAIL}. For privacy-related matters, contact privacy@adforge.ai.`,
  },
];

export default function TermsOfServicePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <div className="bg-stone-950 pt-32 pb-16">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="flex items-center gap-2 mb-4">
            <Link to="/" className="text-stone-500 hover:text-orange-400 text-sm transition-colors">Home</Link>
            <i className="ri-arrow-right-s-line text-stone-600 text-sm" />
            <span className="text-stone-400 text-sm">Terms of Service</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4">Terms of Service</h1>
          <p className="text-stone-400 text-base">
            Last updated: <span className="text-orange-400 font-semibold">{LAST_UPDATED}</span>
          </p>
          <p className="text-stone-400 text-sm mt-4 max-w-2xl leading-relaxed">
            Please read these Terms carefully before using {COMPANY}. They govern your access to and
            use of our platform and services.
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 lg:px-8 py-16">
        <div className="flex flex-col lg:flex-row gap-12">

          {/* Sticky TOC */}
          <aside className="lg:w-56 shrink-0">
            <div className="sticky top-24">
              <p className="text-xs font-bold text-stone-400 uppercase tracking-widest mb-4">On this page</p>
              <nav className="flex flex-col gap-1">
                {sections.map((s) => (
                  <a
                    key={s.id}
                    href={`#${s.id}`}
                    className="text-sm text-stone-500 hover:text-orange-500 transition-colors py-1 leading-snug cursor-pointer"
                  >
                    {s.title}
                  </a>
                ))}
              </nav>
            </div>
          </aside>

          {/* Main */}
          <main className="flex-1 min-w-0">
            <div className="space-y-12">
              {sections.map((section) => (
                <section key={section.id} id={section.id} className="scroll-mt-28">
                  <h2 className="text-xl font-bold text-stone-900 mb-4 pb-3 border-b border-stone-100">
                    {section.title}
                  </h2>

                  {'text' in section && section.text && (
                    <p className="text-stone-600 leading-relaxed text-[15px]">{section.text}</p>
                  )}

                  {'content' in section && section.content && (
                    <div className="space-y-4">
                      {section.content.map((item) => (
                        <div key={item.subtitle} className="bg-stone-50 rounded-xl p-5">
                          <h3 className="text-sm font-bold text-stone-800 mb-2">{item.subtitle}</h3>
                          <p className="text-stone-600 text-[15px] leading-relaxed">{item.text}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  {'bullets' in section && section.bullets && (
                    <>
                      {'intro' in section && section.intro && (
                        <p className="text-stone-600 text-[15px] mb-4">{section.intro}</p>
                      )}
                      <ul className="space-y-2.5">
                        {section.bullets.map((b) => (
                          <li key={b} className="flex items-start gap-3">
                            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-orange-400 shrink-0" />
                            <span className="text-stone-600 text-[15px] leading-relaxed">{b}</span>
                          </li>
                        ))}
                      </ul>
                      {'footer' in section && section.footer && (
                        <p className="mt-4 text-stone-500 text-sm italic">{section.footer}</p>
                      )}
                    </>
                  )}
                </section>
              ))}
            </div>

            {/* Bottom CTA */}
            <div className="mt-16 bg-stone-950 rounded-2xl p-8 text-center">
              <p className="text-white font-semibold text-lg mb-2">Questions about our Terms?</p>
              <p className="text-stone-400 text-sm mb-6">Our legal team is happy to clarify anything you need.</p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a
                  href={`mailto:${CONTACT_EMAIL}`}
                  className="inline-flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold px-6 py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-mail-line" />
                  Contact Legal Team
                </a>
                <Link
                  to="/privacy-policy"
                  className="inline-flex items-center justify-center gap-2 bg-stone-800 hover:bg-stone-700 text-white text-sm font-semibold px-6 py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-shield-check-line" />
                  View Privacy Policy
                </Link>
              </div>
            </div>
          </main>
        </div>
      </div>

      <Footer />
    </div>
  );
}
